import { useState } from "react";
import { Link } from "react-router-dom";
import { CartaoDeConta } from "@/features/auth/components/CartaoDeConta";
import { api } from "@/utils/api";
import { apiErrorMessage } from "@/features/auth/utils/apiError";

/**
 * "Solicitar acesso" — pública, para quem NÃO é da rede estadual de Alagoas (28/09/2026).
 *
 * Ao enviar, a mensagem é sempre a mesma — "Pedido recebido. Confira o seu e-mail." —,
 * tenha o e-mail conta ou não, seja da rede ou não. O que muda chega no e-mail, que só o
 * dono lê. Ver `backend/src/services/pedidoDeAcessoService.ts`.
 *
 * Guarda só o que o formulário pede: nenhum documento, nenhum anexo.
 */
const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
const REDES = [["ESTADUAL", "Estadual"], ["MUNICIPAL", "Municipal"], ["PRIVADA", "Privada"], ["UNIVERSIDADE", "Universidade"], ["OUTRA", "Outra"]] as const;
const MOTIVOS = [["CONHECER", "Conhecer o produto"], ["USAR_COM_ALUNOS", "Usar com os meus alunos"], ["PESQUISA", "Pesquisa"]] as const;

const campo = "w-full border p-2 mt-1";

const SolicitarAcessoPage = () => {
  const [f, setF] = useState({ nome: "", email: "", escola: "", rede: "", cidade: "", uf: "", motivo: "", observacao: "" });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [recebido, setRecebido] = useState("");
  const muda = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    try {
      setEnviando(true);
      const r = await api.post("/api/pedidos-de-acesso", f);
      setRecebido(r.data?.mensagem ?? "Pedido recebido. Confira o seu e-mail.");
    } catch (err: unknown) {
      setErro(apiErrorMessage(err, "Não foi possível enviar. Tente de novo."));
    } finally {
      setEnviando(false);
    }
  };

  if (recebido) {
    return (
      <CartaoDeConta titulo={"Solicitar acesso"}>
        <p className="mt-4 rounded border border-green-200 bg-green-50 p-3 text-green-900">{recebido}</p>
        <p className="mt-3 text-sm text-gray-600">
          O pedido só chega a quem decide depois que você abrir o link que enviamos. Se não encontrar a mensagem,
          olhe também a caixa de spam.
        </p>
        <p className="mt-6 text-center text-sm"><Link to="/login" className="text-marca-600 hover:underline">Voltar para a entrada</Link></p>
      </CartaoDeConta>
    );
  }

  return (
    <CartaoDeConta titulo={"Solicitar acesso"}>
      <p className="text-gray-600 text-sm">
        Para professores e pesquisadores de fora da rede estadual de Alagoas. O pedido é lido por uma pessoa, e a
        resposta chega por e-mail.
      </p>
      <p className="mt-3 rounded border border-marca-100 bg-marca-50 p-3 text-sm text-marca-900">
        É professor da rede estadual de Alagoas (SEDUC-AL)? Não precisa de pedido:{" "}
        <Link to="/cadastro" className="font-medium underline">cadastre-se com o e-mail institucional</Link>, ou entre com o Google.
      </p>

      <form onSubmit={enviar} noValidate className="mt-2">
        <label className="block mt-4" htmlFor="pa-nome"><span className="font-medium">Nome completo</span>
          <input id="pa-nome" autoComplete="name" className={campo} value={f.nome} onChange={muda("nome")} maxLength={120} />
        </label>
        <label className="block mt-4" htmlFor="pa-email"><span className="font-medium">E-mail</span>
          <input id="pa-email" type="email" autoComplete="email" className={campo} value={f.email} onChange={muda("email")} maxLength={200} />
          <span className="block text-xs text-gray-500 mt-1">O institucional, se você tiver; o pessoal, se não tiver. É para ele que vai a confirmação.</span>
        </label>
        <label className="block mt-4" htmlFor="pa-escola"><span className="font-medium">Escola ou instituição</span>
          <input id="pa-escola" className={campo} value={f.escola} onChange={muda("escola")} maxLength={160} />
        </label>
        <label className="block mt-4" htmlFor="pa-rede"><span className="font-medium">Rede</span>
          <select id="pa-rede" className={campo} value={f.rede} onChange={muda("rede")}>
            <option value="">Escolha…</option>
            {REDES.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select>
        </label>
        <div className="mt-4 flex gap-3">
          <label className="block flex-1" htmlFor="pa-cidade"><span className="font-medium">Cidade</span>
            <input id="pa-cidade" autoComplete="address-level2" className={campo} value={f.cidade} onChange={muda("cidade")} maxLength={100} />
          </label>
          <label className="block w-24" htmlFor="pa-uf"><span className="font-medium">UF</span>
            <select id="pa-uf" className={campo} value={f.uf} onChange={muda("uf")}>
              <option value="">—</option>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>
        <fieldset className="mt-4">
          <legend className="font-medium">Para quê</legend>
          {MOTIVOS.map(([v, r]) => (
            <label key={v} className="mt-1 flex items-center gap-2 text-sm">
              <input type="radio" name="motivo" value={v} checked={f.motivo === v} onChange={muda("motivo")} /> {r}
            </label>
          ))}
        </fieldset>
        <label className="block mt-4" htmlFor="pa-obs"><span className="font-medium">Algo mais? <span className="font-normal text-gray-500">(opcional)</span></span>
          <textarea id="pa-obs" rows={3} className={campo} value={f.observacao} onChange={muda("observacao")} maxLength={300} />
          <span className="block text-xs text-gray-500 mt-1">{f.observacao.length}/300. Não envie documentos nem dados de alunos.</span>
        </label>

        {erro && <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-800">{erro}</p>}
        <button type="submit" disabled={enviando} className="mt-6 w-full rounded bg-marca-600 px-4 py-2 font-medium text-white hover:bg-marca-700 disabled:opacity-50">
          {enviando ? "Enviando…" : "Enviar pedido"}
        </button>
        <p className="mt-3 text-xs text-gray-500">
          Guardamos só o que está neste formulário, para decidir e responder. Veja a <Link to="/privacidade" className="underline">política de privacidade</Link>.
        </p>
      </form>
    </CartaoDeConta>
  );
};

export default SolicitarAcessoPage;
